import React from "react";
import Cards, { SmallCardContent } from "../../../../components/commonComponents/Cards/Cards";
import { Box, Table, TableBody, TableContainer, TableRow, Typography } from "@mui/material";
import { StyledTableCell } from "../../../../components/commonComponents/TabularLists/styles";
import EditStoreInfo from "../../ModalContent/EditStoreInfo";
import { checkDeviceStatus, fetchConfigureLocationData, fetchScheduleOverview } from "../../../../api_calls/infrastructure";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deleteItems } from "../../../../Store/actions";
import {
  HtmlTooltip,
  StyledInformationSpan,
} from "../../../../components/commonComponents/StyledComponents/styled";
import { checkUserPermissions } from "../../../../api_calls/userManagement";
import { formatDateDMY } from "../../../../api_calls/utils";

const formatDate = (dateStr) => (dateStr ? formatDateDMY(dateStr) : "—");

const WEEKDAY_UPPER_ORDER = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const WEEKDAY_UPPER_SHORT = {
  MONDAY: "Mon", TUESDAY: "Tue", WEDNESDAY: "Wed", THURSDAY: "Thu",
  FRIDAY: "Fri", SATURDAY: "Sat", SUNDAY: "Sun",
};

const is24hSlot = (slot) => {
  const start = (slot.start || "").slice(0, 5);
  const end = (slot.end || "").slice(0, 5);
  return start === "00:00" && (end === "23:59" || end === "23:58");
};

const formatTimeSlot = (slot) =>
  (slot.start || "").slice(0, 5) + " – " + (slot.end || "").slice(0, 5);

const sortTimeSlots = (slots) =>
  [...slots].sort((a, b) => ((a.start || "") > (b.start || "") ? 1 : -1));

// ISO dates (YYYY-MM-DD) sort lexicographically. Copy first so Redux state is
// not mutated; sort before slicing so the five visible rows are the soonest.
const sortUpcomingByDate = (entries, dateKey) =>
  [...entries].sort((a, b) => (a[dateKey] || "").localeCompare(b[dateKey] || ""));

const groupCurrentOpen = (currentOpen) => {
  if (!currentOpen) return [];
  const timeKey = (day) => JSON.stringify(currentOpen[day] || []);
  const timeRanges = (day) => {
    const slots = currentOpen[day] || [];
    if (slots.length === 0) return ["Closed"];
    const sorted = sortTimeSlots(slots);
    if (sorted.length === 1 && is24hSlot(sorted[0])) return ["Open 24 Hours"];
    return sorted.map(formatTimeSlot);
  };
  const rows = [];
  let groupStart = 0;
  for (let i = 1; i <= WEEKDAY_UPPER_ORDER.length; i++) {
    if (i === WEEKDAY_UPPER_ORDER.length || timeKey(WEEKDAY_UPPER_ORDER[groupStart]) !== timeKey(WEEKDAY_UPPER_ORDER[i])) {
      const startDay = WEEKDAY_UPPER_ORDER[groupStart];
      const endDay = WEEKDAY_UPPER_ORDER[i - 1];
      const label = groupStart === i - 1
        ? WEEKDAY_UPPER_SHORT[startDay]
        : WEEKDAY_UPPER_SHORT[startDay] + " – " + WEEKDAY_UPPER_SHORT[endDay];
      rows.push({ label, ranges: timeRanges(startDay) });
      groupStart = i;
    }
  }
  return rows;
};

const specialEntryRanges = (entry) => {
  const slots = Array.isArray(entry.open_hours) ? entry.open_hours : [];
  if (entry.type === "Closed" || slots.length === 0) return ["Closed"];
  const sorted = sortTimeSlots(slots);
  if (sorted.length === 1 && is24hSlot(sorted[0])) return ["Open 24 Hours"];
  return sorted.map(formatTimeSlot);
};

const quietEntryRanges = (entry) => sortTimeSlots(entry.quiet_hours || []).map(formatTimeSlot);

// Same label/value column split in every overview tile so the space between
// label and content does not change from card to card.
const OVERVIEW_TABLE_SX = {
  minWidth: "50",
  tableLayout: "fixed",
  "& .MuiTableCell-root": {
    verticalAlign: "top",
    maxWidth: "none",
  },
  "& .MuiTableCell-root.text-wrapper": {
    maxWidth: "none",
  },
  "& .MuiTableCell-root:first-of-type": {
    width: "42%",
  },
};

const HOURS_TABLE_SX = {
  ...OVERVIEW_TABLE_SX,
  "& .MuiTableCell-root": {
    verticalAlign: "top",
    maxWidth: "none",
    borderBottom: "none",
    paddingTop: "6px",
    paddingBottom: "6px",
  },
  "& .MuiTableCell-root.text-wrapper": {
    maxWidth: "none",
  },
  "& .MuiTableCell-root:first-of-type": {
    width: "42%",
    fontWeight: "bold",
  },
};

const StackedTimeRanges = ({ ranges }) => ranges.map((t, i) => <div key={i}>{t}</div>);

const HoursOverviewEmpty = ({ text }) => (
  <Typography variant="body2" sx={{ color: "#62626E", pl: 2 }}>
    {text}
  </Typography>
);

const HoursOverviewTable = ({ children }) => (
  <TableContainer>
    {/* size="small" keeps weekday/date rows compact (default Table padding is 16px). */}
    <Table size="small" sx={HOURS_TABLE_SX}>
      <TableBody>{children}</TableBody>
    </Table>
  </TableContainer>
);

const HoursOverviewMoreRow = ({ remaining }) =>
  remaining > 0 ? (
    <TableRow>
      <StyledTableCell colSpan={2} sx={{ borderBottom: "none", color: "#62626E" }}>
        + {remaining} more
      </StyledTableCell>
    </TableRow>
  ) : null;

const Overview = () => {
  const userStateData = useSelector((state) => state.userSelections);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editSectionName, setEditSectionName] = React.useState("StoreDetail");

  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  const showEditButtons =
    permissionScope.infra_store_list_can_edit === true &&
    permissionScope.infra_store_monitoring_can_view === true;
  const hasFullInfraAccess = permissionScope.infra_store_monitoring_can_view === true;

  const dispatchAction = useDispatch();
  const checkDeviceStatusCode = checkDeviceStatus();
  const { id } = useParams();

  // close edit store details popup
  const closeModal = () => {
    dispatchAction(deleteItems(["new_store_loc_data"]));
    setIsModalOpen(false);
  };

  React.useEffect(() => {
    fetchConfigureLocationData(id);
    fetchScheduleOverview(id);
  }, [id]);
  return (
    <>
      {typeof userStateData.store_overview !== "undefined" &&
        typeof userStateData.store_overview.number_store_retailer !== "undefined" &&
        (userStateData.store_overview.number_store_retailer === "" ||
          userStateData.store_overview.number_store_retailer === null) && (
          <>
            <Box height={15} />
            <StyledInformationSpan sx={{ fontSize: "15px" }}>
              Note: No Retailer Store ID added for this store location. Without this information, no data processing for
              this store location possible.
            </StyledInformationSpan>
            <Box height={20} />
          </>
        )}
      <Cards>
        {typeof userStateData.store_overview !== "undefined" && (
          <>
            {/* --------- store info card ----------- */}
            <SmallCardContent
              showEdit={showEditButtons}
              editAction={(e) => {
                setIsModalOpen(!isModalOpen);
                setEditSectionName("StoreDetail");
              }}
              editTooltipText={"Edit Store Info"}
              cardIcon={<h3 style={{ color: "#AF3241" }}>Store Information</h3>}
            >
              <TableContainer>
                <Table sx={OVERVIEW_TABLE_SX} aria-label="store information">
                  <TableBody>
                    {/* --------- Store Name -------- */}
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Name
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.name}
                      </StyledTableCell>
                    </TableRow>
                    {/*-------- street ---------  */}
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Street & No.
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.address}
                      </StyledTableCell>
                    </TableRow>
                    {/*-------- street ---------  */}
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        ZIP Code
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.zipCode}
                      </StyledTableCell>
                    </TableRow>
                    {/*-------- city ---------  */}
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        City
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.city}
                      </StyledTableCell>
                    </TableRow>
                    {/*-------- Region ---------  */}
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Region
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.region?.name}
                      </StyledTableCell>
                    </TableRow>
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Country
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.country?.name}
                      </StyledTableCell>
                    </TableRow>
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Owner
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.company?.name}
                      </StyledTableCell>
                    </TableRow>
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Time Zone
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {
                          userStateData?.store_overview?.timezone !== null && (
                            <>
                              {"(UTC " + userStateData?.store_overview?.timezone?.utc_offset + ") " + userStateData?.store_overview?.timezone?.name}
                            </>
                          )
                        }

                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </SmallCardContent>
            {/* -------- store infrastructure ----------- */}
            {hasFullInfraAccess && <SmallCardContent
              column="right"
              showEdit={showEditButtons}
              editAction={(e) => {
                setIsModalOpen(!isModalOpen);
                setEditSectionName("StoreInfrastructure");
              }}
              editTooltipText={"Edit Store Infra"}
              cardIcon={<h3 style={{ color: "#AF3241" }}>Store Infrastructure</h3>}
            >
              <TableContainer>
                <Table sx={OVERVIEW_TABLE_SX} aria-label="store information">
                  <TableBody>
                    {/* --------- store Name -------- */}
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Cyreen Store ID
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.number_store_cyreen}
                      </StyledTableCell>
                    </TableRow>
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Retailer Store ID
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.number_store_retailer === null
                          ? "N/A"
                          : userStateData?.store_overview?.number_store_retailer}
                      </StyledTableCell>
                    </TableRow>
                    {/*-------- street ---------  */}
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        IP Address
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        <HtmlTooltip title="Store IP Address can be adjusted in the Devices section">
                          {userStateData?.db_store_ip ? (
                            <strong>
                              10.{userStateData?.db_store_ip?.second_octet}.{userStateData?.db_store_ip?.third_octet}.0/24
                            </strong>
                          ) : (
                            <strong>__</strong>
                          )}
                        </HtmlTooltip>
                      </StyledTableCell>
                    </TableRow>
                    {/*-------- street ---------  */}
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Store Status
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {checkDeviceStatusCode === true ? "Active" : "Inactive"}
                      </StyledTableCell>
                    </TableRow>
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Data Processing
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.store_status === 1 ? "Active" : "Inactive"}
                      </StyledTableCell>
                    </TableRow>
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Checkout Tracking
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.automated === true ? "Automated" : "Manual/No"}
                      </StyledTableCell>
                    </TableRow>

                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Equipped Trolleys
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.installed_trolleys}
                      </StyledTableCell>
                    </TableRow>
                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Equipped Baskets
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.installed_baskets}
                      </StyledTableCell>
                    </TableRow>

                    <TableRow>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        Store Size (in m&sup2;)
                      </StyledTableCell>
                      <StyledTableCell component="td" scope="row" className="text-wrapper">
                        {userStateData?.store_overview?.store_size}
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </SmallCardContent>}
            {/* --------- Current Opening Hours ----------- */}
            <SmallCardContent
              showEdit={false}
              cardIcon={<h3 style={{ color: "#AF3241" }}>Current Opening Hours</h3>}
            >
              {(() => {
                const rows = groupCurrentOpen(userStateData?.store_schedule_overview?.current_open);
                return rows.length > 0 ? (
                  <HoursOverviewTable>
                    {rows.map((row, idx) => (
                      <TableRow key={idx}>
                        <StyledTableCell component="td" scope="row" className="text-wrapper">
                          {row.label}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row" className="text-wrapper">
                          <StackedTimeRanges ranges={row.ranges} />
                        </StyledTableCell>
                      </TableRow>
                    ))}
                  </HoursOverviewTable>
                ) : (
                  <HoursOverviewEmpty text="No opening hours available." />
                );
              })()}
            </SmallCardContent>

            {/* --------- Upcoming Special Hours ----------- */}
            <SmallCardContent
              showEdit={false}
              cardIcon={<h3 style={{ color: "#AF3241" }}>Upcoming Special Hours</h3>}
            >
              {(() => {
                const entries = sortUpcomingByDate(
                  userStateData?.store_schedule_overview?.upcoming_special || [],
                  "valid_on"
                );
                const visible = entries.slice(0, 5);
                const remaining = entries.length - visible.length;

                if (visible.length === 0) {
                  return <HoursOverviewEmpty text="No Special Hours in the next 30 days." />;
                }

                return (
                  <HoursOverviewTable>
                    {visible.map((entry) => (
                      <TableRow key={entry.id}>
                        <StyledTableCell component="td" scope="row" className="text-wrapper">
                          {formatDate(entry.valid_on)}
                          {entry.name ? " - " + entry.name : ""}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row" className="text-wrapper">
                          <StackedTimeRanges ranges={specialEntryRanges(entry)} />
                        </StyledTableCell>
                      </TableRow>
                    ))}
                    <HoursOverviewMoreRow remaining={remaining} />
                  </HoursOverviewTable>
                );
              })()}
            </SmallCardContent>

            {/* --------- Upcoming Quiet Hours ----------- */}
            <SmallCardContent
              showEdit={false}
              cardIcon={<h3 style={{ color: "#AF3241" }}>Upcoming Quiet Hours</h3>}
            >
              {(() => {
                const entries = sortUpcomingByDate(
                  userStateData?.store_schedule_overview?.upcoming_quiet || [],
                  "date"
                );
                const visible = entries.slice(0, 5);
                const remaining = entries.length - visible.length;

                if (visible.length === 0) {
                  return <HoursOverviewEmpty text="No Quiet Hours in the next 30 days." />;
                }

                return (
                  <HoursOverviewTable>
                    {visible.map((entry, idx) => (
                      <TableRow key={idx}>
                        <StyledTableCell component="td" scope="row" className="text-wrapper">
                          {formatDate(entry.date)}
                        </StyledTableCell>
                        <StyledTableCell component="td" scope="row" className="text-wrapper">
                          <StackedTimeRanges ranges={quietEntryRanges(entry)} />
                        </StyledTableCell>
                      </TableRow>
                    ))}
                    <HoursOverviewMoreRow remaining={remaining} />
                  </HoursOverviewTable>
                );
              })()}
            </SmallCardContent>
          </>
        )}
      </Cards>
      {isModalOpen && (
        <EditStoreInfo
          open={isModalOpen}
          size={"md"}
          usedFor="update"
          sectionToEdit={editSectionName}
          closeModal={closeModal}
        />
      )}
    </>
  );
};

export default Overview;
